import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { X, ChevronRight, Download, Layers, FileText } from "lucide-react";
import { useDrill, mergedFilters } from "../store/drillStore";
import { useData } from "../store/dataStore";
import { MODULES, FIELD_LABELS } from "../query/drillPaths";
import { runDrill } from "../query/runDrill";
import { fmtValue, fmtFull } from "../query/format";
import { useLabeler } from "./labels";
import { ContextMenu, useCtxMenu } from "./Drillable";
import { exportRowsXlsx } from "../ingest/ingest";

const LEAF_COLS = {
  fact_arr_movement: ["movement_id", "account_name", "type", "amount", "effective_date"],
  fact_bookings: ["deal_id", "account_name", "rep_name", "stage", "amount", "created_at"],
  fact_invoice_line: ["line_id", "invoice_id", "product", "quantity", "amount", "period"],
  fact_gl: ["entry_id", "account", "vendor", "amount", "period"],
};

function LeafRecord({ record, cfg, trace, stageHistory, labeler }) {
  return (
    <div className="leaf-card mt-3" data-testid="leaf-record-card">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} style={{ color: "var(--teal-d)" }} />
        <span className="font-bold text-sm" style={{ color: "var(--navy)" }}>Atomic record — {cfg.leafLabel}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {Object.entries(record).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-dashed pb-1" style={{ borderColor: "var(--line)" }}>
            <span style={{ color: "var(--slate)" }}>{k}</span>
            <span className="font-semibold text-right" style={{ color: "var(--navy)" }}>{k === "amount" ? fmtFull(v) : String(v ?? "—")}</span>
          </div>
        ))}
      </div>
      {stageHistory?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "var(--slate)" }}>Stage history (leaf events)</div>
          {stageHistory.map((s) => (
            <div key={`${s.from_stage}-${s.to_stage}`} className="flex items-center gap-2 py-1 text-xs">
              <span style={{ color: "var(--slate)" }}>{s.from_stage || "—"}</span>
              <ChevronRight size={11} style={{ color: "var(--teal-d)" }} />
              <span className="font-semibold" style={{ color: "var(--navy)" }}>{s.to_stage}</span>
              {s.days_in_prev_stage != null && <span className="ml-auto" style={{ color: "var(--slate)" }}>{s.days_in_prev_stage}d in prev</span>}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "var(--slate)" }}>Drill trace — filters that isolated this record</div>
        <div className="flex flex-wrap gap-1.5">
          {trace.map((e) => (
            <span key={e.field} className="px-2 py-0.5 rounded text-[11px] font-semibold" style={{ background: "var(--navy)", color: "var(--cyan)" }}>
              {FIELD_LABELS[e.field] || e.field} = {labeler(e.field, e.value)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function SheetCrumbs({ stack, popTo, labeler }) {
  return (
    <div className="flex items-center flex-wrap gap-1 mt-3 text-xs" data-testid="drill-breadcrumb">
      <span className="crumb" style={{ color: "rgba(255,255,255,0.7)" }} onClick={() => popTo(0)} data-testid="crumb-root">All</span>
      {stack.map((e, i) => (
        <span key={`${e.field}:${e.value}`} className="flex items-center gap-1">
          <ChevronRight size={11} style={{ color: "var(--teal)" }} />
          <span className={`crumb ${i === stack.length - 1 ? "font-bold" : ""}`} style={{ color: i === stack.length - 1 ? "var(--cyan)" : "rgba(255,255,255,0.7)" }} onClick={() => popTo(i + 1)} data-testid={`crumb-${i}`}>
            {labeler(e.field, e.value)}
          </span>
        </span>
      ))}
    </div>
  );
}

function GroupTable({ result, level, labeler, cfg, maxAbs, onDrill, onAltDrill }) {
  return (
    <table className="dtable" data-testid="drill-table">
      <thead><tr><th>{FIELD_LABELS[level] || level}</th><th className="text-right">Value</th><th className="text-right">Rows</th><th>Share</th></tr></thead>
      <tbody>
        {result.map((r, i) => (
          <tr key={String(r[level])} className="cursor-pointer" data-testid={`drill-row-${i}`}
            onClick={() => onDrill(r)}
            onContextMenu={(e) => onAltDrill(e, r)}
            title="Click to drill · right-click for drill dimensions">
            <td className="font-semibold" style={{ color: "var(--navy)" }}>{labeler(level, r[level])}</td>
            <td className="text-right fact-cell">{fmtValue(r.value, cfg.format)}</td>
            <td className="text-right num" style={{ color: "var(--slate)" }}>{r._count}</td>
            <td><div className="share-bar"><div className="share-fill" style={{ width: `${(Math.abs(r.value) / maxAbs) * 100}%`, background: r.value < 0 ? "var(--bad)" : "var(--teal)" }} /></div></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LeafTable({ result, leafCols, idField, selectedLeaf, onSelect }) {
  return (
    <table className="dtable" data-testid="leaf-table">
      <thead><tr>{leafCols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>
        {result.slice(0, 100).map((r, i) => (
          <tr key={r[idField] ?? i} className="cursor-pointer" data-testid={`leaf-row-${i}`} onClick={() => onSelect(r)}
            style={selectedLeaf === r ? { background: "var(--bg)" } : undefined}>
            {leafCols.map((c) => <td key={c} className={c === "amount" ? "num text-right" : ""} style={{ color: "var(--navy)" }}>{c === "amount" ? fmtFull(r[c]) : String(r[c] ?? "—")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DrillSheet() {
  const { open, module, stack, next, push, popTo, close } = useDrill();
  const tables = useData((s) => s.tables);
  const labeler = useLabeler();
  const [selectedLeaf, setSelectedLeaf] = useState(null);
  const [menu, setMenu, closeMenu] = useCtxMenu();

  useEffect(() => setSelectedLeaf(null), [stack.length, module]);

  const cfg = module ? MODULES[module] : null;
  const rows = useMemo(() => (cfg ? tables[cfg.table] || [] : []), [cfg, tables]);
  const filters = useMemo(() => ({ ...(cfg?.baseFilters || {}), ...mergedFilters(stack) }), [cfg, stack]);
  const filteredFields = useMemo(() => new Set(Object.keys(filters)), [filters]);
  const remaining = cfg ? cfg.path.filter((f) => !filteredFields.has(f)) : [];
  const level = next && !filteredFields.has(next) ? next : remaining[0];
  const isLeaf = !level;

  const result = useMemo(() => {
    if (!cfg) return [];
    if (isLeaf) return runDrill(rows, { filters });
    return runDrill(rows, { measure: cfg.measure, groupBy: [level], filters, orderBy: ["month", "period", "cohort", "stage"].includes(level) ? "dim" : undefined });
  }, [cfg, rows, filters, level, isLeaf]);

  if (!open || !cfg) return null;

  const total = isLeaf ? result.reduce((s, r) => s + (Number(r.amount) || 0), 0) : result.reduce((s, r) => s + r.value, 0);
  const maxAbs = isLeaf ? 1 : Math.max(...result.map((r) => Math.abs(r.value)), 1);
  const chartData = isLeaf ? [] : result.slice(0, 12).map((r) => ({ name: labeler(level, r[level]), value: r.value, raw: r[level] }));
  const leafCols = LEAF_COLS[cfg.table] || Object.keys(result[0] || {}).slice(0, 5);

  const rowContextMenu = (e, entry) => {
    e.preventDefault();
    const alts = cfg.altDims.filter((d) => !filteredFields.has(d) && d !== level && d !== entry.field);
    if (!alts.length) return;
    setMenu({
      x: e.clientX, y: e.clientY,
      items: alts.map((dim) => ({ dim, onClick: (ev) => { ev.stopPropagation(); closeMenu(); push(entry, dim); } })),
    });
  };

  return (
    <AnimatePresence>
      <motion.div key="overlay" className="fixed inset-0 z-40" style={{ background: "rgba(11,31,58,0.35)", backdropFilter: "blur(2px)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} data-testid="drill-overlay" />
      <motion.div key="sheet" className="fixed right-0 top-0 h-screen w-[640px] max-w-[100vw] z-50 overflow-y-auto" style={{ background: "var(--white)", boxShadow: "-8px 0 32px rgba(11,31,58,0.15)", borderLeft: "1px solid var(--line)" }} initial={{ x: 640 }} animate={{ x: 0 }} exit={{ x: 640 }} transition={{ type: "tween", duration: 0.25 }} data-testid="drill-sheet">
        <div className="sticky top-0 z-10 px-6 pt-5 pb-4" style={{ background: "linear-gradient(90deg, var(--navy), var(--navy2))" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Layers size={16} style={{ color: "var(--cyan)" }} />
              <span className="font-black tracking-tight" style={{ fontFamily: "Chivo" }}>{cfg.title}</span>
            </div>
            <button onClick={close} data-testid="drill-close" className="text-white/70 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          <SheetCrumbs stack={stack} popTo={popTo} labeler={labeler} />
        </div>

        <div className="px-6 py-5">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--slate)" }}>
                {isLeaf ? `Leaf level — ${result.length} atomic ${cfg.leafLabel}${result.length === 1 ? "" : "s"}` : `By ${FIELD_LABELS[level] || level} · ${result.length} groups`}
              </div>
              <div className="text-2xl font-black num mt-1" style={{ color: "var(--navy)", fontFamily: "Chivo" }} data-testid="drill-total">{fmtValue(total, cfg.format)}</div>
            </div>
            <button className="btn-util" data-testid="btn-export-drill-xlsx" onClick={() => exportRowsXlsx(result, `drill_${module}_${isLeaf ? "leaf" : level}.xlsx`)}>
              <Download size={13} /> XLSX
            </button>
          </div>

          {!isLeaf && chartData.length > 1 && (
            <div className="panel p-3 mb-4" style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--slate)" }} interval={0} angle={-20} textAnchor="end" height={44} />
                  <YAxis hide />
                  <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                    <div className="rounded-md shadow-lg px-3 py-2 text-xs font-mono" style={{ background: "var(--navy)", color: "#fff" }}>
                      <div className="font-bold" style={{ color: "var(--cyan)" }}>{label}</div>
                      <div style={{ color: "var(--teal)" }}>{fmtValue(payload[0].value, cfg.format)}</div>
                    </div>) : null} />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d) => d && push({ field: level, value: d.raw })}>
                    {chartData.map((d) => <Cell key={String(d.raw)} fill={d.value < 0 ? "var(--bad)" : "var(--teal)"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {!isLeaf ? (
            <GroupTable result={result} level={level} labeler={labeler} cfg={cfg} maxAbs={maxAbs}
              onDrill={(r) => push({ field: level, value: r[level] })}
              onAltDrill={(e, r) => rowContextMenu(e, { field: level, value: r[level] })} />
          ) : (
            <>
              <LeafTable result={result} leafCols={leafCols} idField={cfg.idField} selectedLeaf={selectedLeaf} onSelect={setSelectedLeaf} />
              {selectedLeaf && (
                <LeafRecord record={selectedLeaf} cfg={cfg} trace={stack} labeler={labeler}
                  stageHistory={cfg.table === "fact_bookings" ? (tables.fact_stage_history || []).filter((s) => s.deal_id === selectedLeaf.deal_id) : null} />
              )}
              {!selectedLeaf && result.length > 0 && <div className="text-xs mt-3 text-center" style={{ color: "var(--slate)" }}>Click a row to open the atomic record + drill trace</div>}
            </>
          )}
        </div>
        <ContextMenu menu={menu} onClose={closeMenu} />
      </motion.div>
    </AnimatePresence>
  );
}
